import React, { useMemo, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { VOICES } from "../data/voices";
import VoiceInquiryForm from "../components/voices/VoiceInquiryForm";
import OptimizedImage from "../components/media/OptimizedImage";
import {
  ArrowRight, ArrowLeft, Sparkles, ChevronRight, Lock, ShieldCheck,
} from "lucide-react";

/**
 * SAT Stimmen™ Sales-Page — konfigurationsgetrieben.
 * Ein Layout für alle 6 Produkte. Preis auf Anfrage — Anfrage-first-Modell.
 */
const VoiceSalesPage = () => {
  const { slug } = useParams();
  const voice = useMemo(() => VOICES[slug], [slug]);
  const formRef = useRef(null);

  if (!voice) return <Navigate to="/stimmen" replace />;

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const accentSoft = `${voice.accent}22`;

  return (
    <div className="min-h-screen text-white" style={{ background: voice.bg }} data-testid={`voice-page-${slug}`}>
      <Helmet>
        <title>{`${voice.label} · SAT Akademie`}</title>
        <meta name="description" content={voice.seo_desc} />
      </Helmet>

      {/* Hero */}
      <section className="pt-28 pb-24 relative overflow-hidden">
        {/* Hero-Bild: 16:9 Desktop · 4:5 Mobile · dezent hinter Content */}
        {voice.hero_asset && (
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <OptimizedImage
              assetId={voice.hero_asset}
              ratio="16/9"
              ratioMobile="4/5"
              priority
              className="w-full h-full"
              overlayClassName="bg-gradient-to-b from-black/40 via-black/60 to-[#050b1c]"
            />
          </div>
        )}
        <div className="container-sat max-w-5xl mx-auto px-6 relative z-10">
          <Link to="/stimmen" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white mb-8 tracking-wider uppercase" data-testid="voice-back-to-stimmen">
            <ArrowLeft size={14} /> Alle SAT Stimmen™
          </Link>

          <div className="text-[11px] tracking-[0.32em] uppercase mb-6" style={{ color: voice.accent }}>
            {voice.kicker}
          </div>

          <h1 className="serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] mb-8">
            {voice.title}{" "}
            <em className="italic" style={{ color: voice.accent }}>{voice.title2}</em>
          </h1>

          <p className="text-xl sm:text-2xl text-white/85 leading-relaxed max-w-3xl mb-6 font-light">
            {voice.headline}
          </p>
          <p className="text-base text-white/60 leading-relaxed max-w-3xl mb-12">
            {voice.lead}
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={scrollToForm}
              data-testid="voice-cta-inquiry"
              className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-medium tracking-wider transition-all hover:shadow-[0_20px_60px_-15px_rgba(212,175,55,0.4)]"
              style={{ background: voice.accent, color: "#081328" }}
            >
              <Sparkles size={16} /> Anfrage senden
            </button>
            <div className="inline-flex items-center gap-2 text-sm text-white/60">
              <Lock size={14} /> Preis auf Anfrage — nach persönlichem Gespräch
            </div>
          </div>
        </div>
      </section>

      {/* Who for */}
      <section className="py-20 border-t border-white/5">
        <div className="container-sat max-w-5xl mx-auto px-6">
          <h2 className="serif text-3xl sm:text-4xl font-light mb-10" style={{ color: voice.accent }}>
            {voice.who_for_title}
          </h2>
          <ul className="space-y-4 max-w-3xl">
            {voice.who_for.map((line, i) => (
              <li key={i} className="flex items-start gap-3 text-lg text-white/85 leading-relaxed">
                <ChevronRight size={20} className="mt-1 shrink-0" style={{ color: voice.accent }} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Promises */}
      <section className="py-20 border-t border-white/5">
        <div className="container-sat max-w-6xl mx-auto px-6">
          <h2 className="serif text-3xl sm:text-4xl font-light mb-14">
            {voice.promise_title}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {voice.promises.map((p, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur transition hover:border-white/20"
                data-testid={`voice-promise-${i}`}
              >
                <div className="serif text-xl mb-2" style={{ color: voice.accent }}>{p.t}</div>
                <div className="text-white/70 text-base leading-relaxed">{p.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-20 border-t border-white/5">
        <div className="container-sat max-w-5xl mx-auto px-6">
          <h2 className="serif text-3xl sm:text-4xl font-light mb-14">
            {voice.curriculum_title}
          </h2>
          <div className="space-y-3">
            {voice.curriculum.map((m, i) => (
              <div
                key={i}
                className="flex items-start gap-6 p-5 rounded-xl border border-white/5 hover:border-white/15 hover:bg-white/[0.02] transition"
                data-testid={`voice-module-${i}`}
              >
                <div
                  className="shrink-0 w-14 h-14 rounded-lg flex items-center justify-center serif text-xl border"
                  style={{ borderColor: `${voice.accent}55`, color: voice.accent, background: accentSoft }}
                >
                  {m.n}
                </div>
                <div>
                  <div className="serif text-xl font-light text-white/95 mb-1">{m.t}</div>
                  <div className="text-white/60 text-base leading-relaxed">{m.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formats */}
      <section className="py-20 border-t border-white/5">
        <div className="container-sat max-w-5xl mx-auto px-6">
          <h2 className="serif text-3xl sm:text-4xl font-light mb-10">
            {voice.formats_title}
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
            {voice.formats.map((f, i) => (
              <li
                key={i}
                className="flex items-center gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.02]"
              >
                <ShieldCheck size={16} style={{ color: voice.accent }} />
                <span className="text-white/85">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Inquiry form */}
      <section ref={formRef} className="py-20 border-t border-white/5" data-testid="voice-inquiry-section">
        <div className="container-sat max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="text-[11px] tracking-[0.32em] uppercase mb-4" style={{ color: voice.accent }}>
              Der nächste Schritt
            </div>
            <h2 className="serif text-3xl sm:text-4xl font-light mb-4">
              Sag uns, was dich zu <em className="italic" style={{ color: voice.accent }}>{voice.label.replace("SAT ", "").replace("™", "")}</em> zieht.
            </h2>
            <p className="text-white/60 max-w-xl mx-auto text-base leading-relaxed">
              Wir melden uns innerhalb von 48&nbsp;Stunden mit einem Vorschlag für ein persönliches Gespräch.
            </p>
          </div>
          <VoiceInquiryForm voice={voice} />
        </div>
      </section>

      {/* Footer nav */}
      <section className="py-14 border-t border-white/5">
        <div className="container-sat max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-6">
          <Link to="/stimmen" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Alle SAT Stimmen™ ansehen
          </Link>
          <Link to="/erstgespraech" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-2">
            Unsicher? Kostenloses Erstgespräch <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default VoiceSalesPage;
